import { NextSeo } from 'next-seo'
import Link from 'next/link'
import { ReactNode } from 'react'
import styled, { keyframes } from 'styled-components'
import TokenAmount from 'views/TradingCompetitionV3/components/TokenAmount'
import { COMPETITIONS, competitionPath, legacyPath } from 'views/TradingCompetitionV3/routes'
import { Profile } from './api'
import { HistoryCompetition, useWalletHistory } from './history'
import { ActionBtn, Hint, Icon, IdentityCard, Note, SectionTitle, Surface, useShare } from './Identity'
import { profileSteps } from './progress'

/* ───────────────────────── progress ───────────────────────── */
const HIDDEN = { position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' } as const
/** the step's button stays inside its card on a 360 px phone: full width of the text column up to 360 px, the label may wrap */
const STEP_CTA = { marginTop: 10, width: '100%', maxWidth: 360, whiteSpace: 'normal', textAlign: 'center', lineHeight: '20px', padding: '10px 16px' } as const
const shine = keyframes`from { background-position: 0% 50%; } to { background-position: 200% 50%; }`
const Bar = styled.div`
  height: 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  overflow: hidden;
  i {
    display: block;
    height: 100%;
    min-width: 10px;
    border-radius: inherit;
    background: linear-gradient(90deg, #7fd4e8, #9a7bff, #ffd27a, #7fd4e8);
    background-size: 200% 100%;
    box-shadow: 0 0 16px rgba(127, 212, 232, 0.7);
    transition: width 500ms cubic-bezier(0.2, 0.8, 0.2, 1);
    animation: ${shine} 4s linear infinite;
  }
  @media (prefers-reduced-motion: reduce) {
    i {
      animation: none;
      transition: none;
    }
  }
`
const StepList = styled.ol`
  margin: 14px 0 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 8px;
`
const StepRow = styled.li<{ $state: 'done' | 'next' | 'todo' }>`
  position: relative;
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr);
  gap: 12px;
  align-items: start;
  padding: 12px;
  border-radius: 16px;
  background: ${({ $state }) => ($state === 'next' ? 'linear-gradient(135deg, rgba(127, 212, 232, 0.14), rgba(154, 123, 255, 0.14))' : 'rgba(255, 255, 255, 0.03)')};
  border: 1px solid ${({ $state }) => ($state === 'next' ? 'rgba(127, 212, 232, 0.45)' : 'rgba(255, 255, 255, 0.06)')};
  .dot {
    width: 40px;
    height: 40px;
    border-radius: 14px;
    display: grid;
    place-items: center;
    font-size: 16px;
    font-weight: 800;
    color: ${({ $state }) => ($state === 'todo' ? 'rgba(201, 195, 221, 0.7)' : '#0b1220')};
    background: ${({ $state }) => ($state === 'done' ? 'linear-gradient(135deg, #b8f5d0, #31d0aa)' : $state === 'next' ? 'linear-gradient(135deg, #b9f0ff, #7fd4e8 45%, #b38bff)' : 'rgba(255, 255, 255, 0.06)')};
    box-shadow: ${({ $state }) => ($state === 'next' ? '0 8px 20px -6px rgba(127, 212, 232, 0.75)' : $state === 'done' ? '0 8px 18px -8px rgba(49, 208, 170, 0.7)' : 'none')};
  }
  strong {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 8px;
    font-size: 15px;
    line-height: 20px;
    font-weight: 800;
    color: ${({ theme, $state }) => ($state === 'todo' ? theme.colors.textSubtle : theme.colors.text)};
  }
  p {
    margin: 2px 0 0;
    font-size: 13px;
    line-height: 18px;
    color: ${({ theme }) => theme.colors.textSubtle};
  }
  small {
    font-size: 12px;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 999px;
    color: #c9c3dd;
    background: rgba(255, 255, 255, 0.08);
  }
`

export function ProgressCard({ p, mine, onClaim, onEdit, busy, error }: { p: Profile | null; mine: boolean; onClaim?: () => void; onEdit?: () => void; busy?: boolean; error?: string | null }) {
  const s = profileSteps(p)
  const pct = Math.round((s.done / s.total) * 100)
  return (
    <Surface $lit data-testid="profile-progress">
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
        <SectionTitle as="h2">{s.claimed ? 'Finish your profile' : 'Your profile is ready'}</SectionTitle>
        <strong style={{ fontSize: 15, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          {s.done} <span style={{ opacity: 0.65, fontWeight: 600 }}>of {s.total}</span>
        </strong>
      </div>
      <div style={{ marginTop: 12 }} role="progressbar" aria-valuemin={0} aria-valuemax={s.total} aria-valuenow={s.done} aria-label="Profile progress">
        <Bar>
          <i style={{ width: `${Math.max(pct, 4)}%` }} />
        </Bar>
      </div>
      <StepList>
        {s.steps.map((st, i) => {
          const state = st.done ? 'done' : s.next?.id === st.id ? 'next' : 'todo'
          return (
            <StepRow key={st.id} $state={state} data-testid={`step-${st.id}`} data-state={state}>
              <span className="dot" aria-hidden>
                {st.done ? <Icon name="check" size={20} /> : st.optional ? <Icon name="badge" size={18} /> : i + 1}
              </span>
              <div style={{ minWidth: 0 }}>
                <strong>
                  {st.title}
                  {st.optional && <small>Optional</small>}
                  <span style={HIDDEN}>{st.done ? '(done)' : state === 'next' ? '(next step)' : '(to do)'}</span>
                </strong>
                <p>{st.unlocks}</p>
                {mine && state === 'next' && st.id === 'claim' && onClaim && (
                  <ActionBtn type="button" $primary onClick={onClaim} disabled={busy} data-testid="create-profile" style={STEP_CTA}>
                    <Icon name="signin" size={18} />
                    {busy ? 'Check your wallet…' : 'Claim with one free signature'}
                  </ActionBtn>
                )}
                {mine && state === 'next' && st.id !== 'claim' && onEdit && (
                  <ActionBtn type="button" onClick={onEdit} data-testid={`step-${st.id}-go`} style={STEP_CTA}>
                    <Icon name="edit" size={18} />
                    {st.id === 'handle' ? 'Choose a handle' : 'Pick an avatar'}
                  </ActionBtn>
                )}
              </div>
            </StepRow>
          )
        })}
      </StepList>
      {error && (
        <Note role="alert" style={{ marginTop: 10 }}>
          {error}
        </Note>
      )}
    </Surface>
  )
}

/* ───────────────────────── activity ───────────────────────── */
const Tiles = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  margin-top: 14px;
`
const Tile = styled.a<{ $tone: string }>`
  display: grid;
  align-content: start;
  gap: 2px;
  min-width: 0;
  padding: 12px 10px;
  border-radius: 16px;
  text-decoration: none;
  color: inherit;
  background: radial-gradient(120% 120% at 0% 0%, ${({ $tone }) => $tone}33, transparent 60%), rgba(8, 12, 30, 0.5);
  border: 1px solid ${({ $tone }) => $tone}55;
  box-shadow: 0 12px 26px -18px ${({ $tone }) => $tone};
  b {
    font-size: clamp(24px, 7vw, 32px);
    line-height: 1.05;
    font-weight: 800;
    letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
    color: ${({ $tone }) => $tone};
    text-shadow: 0 0 18px ${({ $tone }) => $tone}66;
  }
  span {
    font-size: 13px;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.text};
  }
  em {
    font-style: normal;
    font-size: 12px;
    line-height: 16px;
    color: ${({ theme }) => theme.colors.textSubtle};
    overflow-wrap: anywhere;
  }
  &:focus-visible {
    outline: 2px solid ${({ $tone }) => $tone};
    outline-offset: 2px;
  }
`
const Section = styled.div`
  margin-top: 16px;
  display: grid;
  gap: 6px;
  scroll-margin-top: 120px;
  h3 {
    margin: 0 0 2px;
    font-size: 14px;
    font-weight: 800;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.textSubtle};
  }
`
const Row = styled.a`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 4px 12px;
  min-height: 52px;
  padding: 8px 12px;
  border-radius: 14px;
  text-decoration: none;
  color: inherit;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  .l {
    min-width: 0;
    display: grid;
    gap: 2px;
  }
  .t {
    font-size: 15px;
    font-weight: 700;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .s {
    font-size: 12px;
    line-height: 16px;
    color: ${({ theme }) => theme.colors.textSubtle};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .r {
    font-size: 14px;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
    text-align: right;
    white-space: nowrap;
  }
  &:hover {
    background: rgba(255, 255, 255, 0.06);
  }
  &:focus-visible {
    outline: 2px solid rgba(127, 212, 232, 0.8);
  }
`
const Chip = styled.span<{ $tone: 'gold' | 'live' | 'muted' }>`
  display: inline-block;
  margin-left: 6px;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  vertical-align: 1px;
  color: ${({ $tone }) => ($tone === 'gold' ? '#1a1300' : $tone === 'live' ? '#062a20' : '#c9c3dd')};
  background: ${({ $tone }) => ($tone === 'gold' ? 'linear-gradient(135deg, #ffe58a, #f0b90b)' : $tone === 'live' ? '#31d0aa' : 'rgba(255,255,255,0.08)')};
`

const usd = (n: number | null | undefined) => (typeof n === 'number' && Number.isFinite(n) ? `$${n >= 1000 ? Math.round(n).toLocaleString('en-US') : n.toFixed(2)}` : '—')
const day = (t: number) => (t ? new Date(t * 1000).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }) : '')
const PHASE: Record<string, string> = { live: 'Live', upcoming: 'Upcoming', ended: 'Ended', settled: 'Finished', refunded: 'Refunded', cancelled: 'Cancelled' }
const LOCK_PHASE: Record<string, string> = { locked: 'Locked', vesting: 'Vesting', due: 'Unlock due', released: 'Released' }

function CompetitionRow({ c }: { c: HistoryCompetition }) {
  const href = c.legacy ? legacyPath(c.id) : competitionPath(c.id)
  const phase = c.phase ? PHASE[c.phase] ?? c.phase : null
  return (
    <Link href={href} passHref legacyBehavior>
      <Row data-testid="activity-competition">
        <span className="l">
          <span className="t">
            Competition #{c.id}
            {c.legacy && <Chip $tone="muted">earlier contract</Chip>}
            {c.winner && <Chip $tone="gold">Winner</Chip>}
            {!c.winner && c.phase === 'live' && <Chip $tone="live">Live</Chip>}
          </span>
          <span className="s">
            {c.rank ? `Rank ${c.rank}${c.entrants ? ` of ${c.entrants}` : ''}` : c.legacy ? 'Prize won' : 'On the board'}
            {phase && c.phase !== 'live' ? ` · ${phase}` : ''}
            {c.claimed === false ? ' · prize to claim' : c.claimed ? ' · prize claimed' : ''}
          </span>
        </span>
        <span className="r">{c.prize ? <TokenAmount raw={c.prize.amount} token={c.prize.token} /> : <Icon name="chevron" size={18} />}</span>
      </Row>
    </Link>
  )
}

export function ActivityCard({ wallet, mine }: { wallet: string; mine: boolean }) {
  const { data, error, isLoading } = useWalletHistory(wallet)
  const head = (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
      <Hint label="activity" text="Public BNB Chain activity of this address through CADINU: trades on CADINUdex and MultiSwap, trading competitions and locks. Anyone can see it on BscScan; nothing private is shown.">
        <SectionTitle as="h2">Activity on CADINU</SectionTitle>
      </Hint>
    </div>
  )
  if (error)
    return (
      <Surface data-testid="activity-card">
        {head}
        <Note style={{ marginTop: 10 }}>The activity could not be read right now. Try again in a minute.</Note>
      </Surface>
    )
  if (isLoading || !data)
    return (
      <Surface data-testid="activity-card" aria-busy="true" style={{ minHeight: 180 }}>
        {head}
        <Note style={{ marginTop: 10 }}>Reading BNB Chain activity…</Note>
      </Surface>
    )
  const { trades, competitions, locks } = data
  return (
    <Surface data-testid="activity-card">
      {head}
      <Tiles>
        <Tile href="#act-trades" $tone="#7fd4e8" data-testid="activity-stat-trades">
          <b>{trades.ok ? trades.count : '—'}</b>
          <span>Trades</span>
          <em>{trades.volumeUsd !== null ? `${usd(trades.volumeUsd)} volume` : 'through CADINU'}</em>
        </Tile>
        <Tile href="#act-competitions" $tone="#ffd27a" data-testid="activity-stat-competitions">
          <b>{competitions.ok ? competitions.joined : '—'}</b>
          <span>Competitions</span>
          <em>{competitions.won ? `${competitions.won} won` : 'joined'}</em>
        </Tile>
        <Tile href="#act-locks" $tone="#b38bff" data-testid="activity-stat-locks">
          <b>{locks.ok ? locks.count : '—'}</b>
          <span>Locks</span>
          <em>{locks.valueUsd !== null ? `${usd(locks.valueUsd)} locked` : 'tokens or LP'}</em>
        </Tile>
      </Tiles>

      {data.empty && (
        <div data-testid="activity-empty" style={{ marginTop: 16, display: 'grid', gap: 12, justifyItems: 'start' }}>
          <Note>{mine ? 'Nothing yet. Your first swap, competition or lock through CADINU shows up here on its own.' : 'No activity through CADINU yet.'}</Note>
          {mine && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8, width: '100%', maxWidth: 360 }}>
              <ActionBtn as="a" href="/swap" $primary>
                Swap
              </ActionBtn>
              <ActionBtn as="a" href={COMPETITIONS}>
                Competitions
              </ActionBtn>
            </div>
          )}
        </div>
      )}

      {trades.items.length > 0 && (
        <Section id="act-trades" data-testid="activity-trades">
          <h3>
            Trades · {trades.count}
          </h3>
          {trades.items.map((t) => (
            <Row key={t.tx} href={`https://bscscan.com/tx/${t.tx}`} target="_blank" rel="noopener noreferrer">
              <span className="l">
                <span className="t">{t.label}</span>
                <span className="s">
                  {t.via === 'multiswap' ? `MultiSwap · ${t.venue}` : t.venue} · {day(t.t)}
                </span>
              </span>
              <span className="r">{usd(t.usd)}</span>
            </Row>
          ))}
          {trades.count > trades.items.length && (
            <Note style={{ fontSize: 13 }}>
              The newest {trades.items.length} of {trades.count}.{' '}
              <a href={`https://bscscan.com/address/${wallet}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline' }}>
                All on BscScan
              </a>
            </Note>
          )}
        </Section>
      )}
      {competitions.items.length > 0 && (
        <Section id="act-competitions" data-testid="activity-competitions">
          <h3>Competitions · {competitions.joined}</h3>
          {competitions.items.map((c) => (
            <CompetitionRow key={`${c.legacy ? 'l' : 'c'}${c.id}`} c={c} />
          ))}
        </Section>
      )}
      {locks.items.length > 0 && (
        <Section id="act-locks" data-testid="activity-locks">
          <h3>Locks · {locks.count}</h3>
          {locks.items.map((l) => (
            <Link key={`${l.type}${l.id}`} href={`/lock/${l.type}/${l.id}`} passHref legacyBehavior>
              <Row>
                <span className="l">
                  <span className="t">{l.title}</span>
                  <span className="s">
                    {LOCK_PHASE[l.phase] ?? l.phase} · {l.phase === 'released' ? 'withdrawn' : l.phase === 'locked' ? `unlocks ${day(l.unlockAt)}` : `since ${day(l.unlockAt)}`}
                  </span>
                </span>
                <span className="r">{l.phase === 'released' ? '—' : usd(l.valueUsd)}</span>
              </Row>
            </Link>
          ))}
        </Section>
      )}
      {(!trades.ok || !competitions.ok || !locks.ok) && (
        <Note style={{ marginTop: 12, fontSize: 13 }}>Part of the activity could not be read right now; it shows again on the next visit.</Note>
      )}
    </Surface>
  )
}

/* ───────────────────────── the page of a wallet that never signed ───────────────────────── */
/**
 * The automatic profile (Bez, 2026-10-07): any wallet's public activity through CADINU, at /u (own wallet) and /u/<0x address>.
 * Visibility rules (docs: cadinu-docs profile guide; NOTE.md of stage-auto-profile-nobnb-20261007):
 *  - nothing is stored for an unclaimed wallet; an address alone never creates a CADINU profile (the social API is unchanged);
 *  - reachable only by its address URL: never by a handle, never in followers lists, search or the people lists;
 *  - leaderboards keep showing a short address for it (the batch profile lookup answers "none", which carries no name);
 *  - the page is noindex, carries no name, no avatar and no counts, and is marked "Unclaimed";
 *  - only the owner sees the claim step; a visitor cannot follow it.
 */
export function UnclaimedProfile({ wallet, mine, onClaim, busy, error, children }: { wallet: string; mine: boolean; onClaim?: () => void; busy?: boolean; error?: string | null; children?: ReactNode }) {
  const p: Profile = { state: 'none', address: wallet }
  const url = typeof window !== 'undefined' ? `${window.location.origin}/u/${wallet}` : ''
  const { share, done } = useShare(url, 'CADINU activity')
  return (
    <div data-testid="auto-profile" style={{ display: 'grid', gap: 'inherit' }}>
      <NextSeo noindex nofollow title="CADINU profile" />
      <IdentityCard
        p={p}
        actions={
          mine ? (
            <ActionBtn type="button" onClick={share} data-testid="share-btn" aria-label="Share profile">
              <Icon name={done ? 'check' : 'share'} size={18} />
              {done ? 'Link copied' : 'Share'}
            </ActionBtn>
          ) : undefined
        }
      >
        <Note style={{ marginTop: 12 }} data-testid="auto-profile-note">
          {mine
            ? 'This page is already yours: it shows your public BNB Chain activity through CADINU. No signature was needed to see it.'
            : 'This wallet has not claimed its CADINU profile. Everything here is public BNB Chain activity of the address.'}
        </Note>
        {children}
      </IdentityCard>
      {mine && <ProgressCard p={null} mine onClaim={onClaim} busy={busy} error={error} />}
      <ActivityCard wallet={wallet} mine={mine} />
    </div>
  )
}
